import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Crosshair, Eye, FolderOpen, Layers, List, Plus, Save, Search } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent } from '../ui/context-menu';
import { MenuItems } from './DatasetMenu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { colormapGradient } from '../../lib/raster-api';
import { CRS_OPTIONS } from '../../lib/coordinates';
import { buildTree, visibleRows, keyIntent, GROUP_LABEL, subCollapseKey } from '../../lib/layer-tree';
import { layerMenuItems, runLayerMenuAction, datasetMenuItems, runDatasetMenuAction } from '../../lib/layer-menu';
import { useDragReorder } from '../../hooks/useDragReorder';
import { useTheme } from '../../contexts/ThemeContext';
import SaveLayersDialog from './SaveLayersDialog';
import OpenDatasetPopover, { popoverPosBelow } from './OpenDatasetPopover';

/**
 * Dense layer tree for the left dock (the `layers` panel, lib/panel-registry.js): 28 px rows
 * grouped by kind, drag-reorder (draw order), right-click menu, search / visible-only filter and
 * keyboard control. Model and handlers come from hooks/useLayerTree; the pure logic lives in
 * lib/layer-tree.js and lib/layer-menu.js. Row controls:
 *   checkbox = visibility, swatch = current style, double-click row = zoom (loads a lazy project dataset), F2 = rename,
 *   hover = opacity slider + zoom-to, Delete = remove (confirm), Space = toggle.
 */

const ROW_H = 28;
const fmtCount = (n) => (n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

export function Swatch({ swatch }) {
  if (!swatch) return null;
  const base = { flexShrink: 0, display: 'inline-block' };
  if (swatch.type === 'ramp') {
    const bg = swatch.colormap ? colormapGradient(swatch.colormap) : `linear-gradient(to right, ${(swatch.colors || []).join(', ')})`;
    return <span data-testid="swatch" data-swatch="ramp" style={{ ...base, width: 18, height: 8, borderRadius: 2, background: bg }} />;
  }
  const color = swatch.color || '#8b949e';
  if (swatch.shape === 'line') return <span data-testid="swatch" data-swatch="line" style={{ ...base, width: 16, height: 3, borderRadius: 2, background: color }} />;
  if (swatch.shape === 'fill') return <span data-testid="swatch" data-swatch="fill" style={{ ...base, width: 12, height: 10, borderRadius: 2, background: color, opacity: 0.85, border: `1px solid ${swatch.stroke || color}` }} />;
  return <span data-testid="swatch" data-swatch="point" style={{ ...base, width: 10, height: 10, borderRadius: '50%', background: color, border: `1.5px solid ${swatch.stroke || 'rgba(255,255,255,0.5)'}` }} />;
}

function RenameInput({ initial, onDone }) {
  const [text, setText] = useState(initial);
  const done = useRef(false);
  const finish = (commit) => { if (done.current) return; done.current = true; onDone(commit ? text : null); };
  return (
    <input autoFocus value={text} aria-label="Layer name" data-no-drag
      onFocus={(e) => e.target.select()}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') finish(true);
        else if (e.key === 'Escape') finish(false);
      }}
      onClick={(e) => e.stopPropagation()}
      style={{ flex: 1, minWidth: 0, height: 20, fontSize: 11, padding: '0 4px', borderRadius: 4, color: 'var(--gs-text-0)', background: 'var(--gs-bg-4)', border: '1px solid var(--gs-accent)', outline: 'none' }} />
  );
}

function LayerRow({ entry, focused, active, drag, rowProps, actions, renaming, onRenameDone, onStartRename, onConfirmRemove, onFocusRow, setRowRef, showGroup, rowIdx, menuCloseRef, datasetActions, nested }) {
  const items = layerMenuItems(entry, { isActive: active });
  const dropEdge = drag?.overKey === entry.key ? drag.position : null;
  const run = (id) => {
    if (id === 'rename') menuCloseRef.current = true;
    runLayerMenuAction(id, entry, actions, { startRename: onStartRename, confirmRemove: onConfirmRemove, datasetActions });
  };
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={(el) => setRowRef(entry.key, el)}
          role="treeitem" aria-level={showGroup ? 1 : nested ? 3 : 2} aria-selected={active} aria-label={entry.name}
          tabIndex={focused ? 0 : -1}
          data-testid={entry.isDataset ? 'dataset-layer' : 'layer-row'} data-layer-row={entry.key}
          title={(entry.group === 'viewing' ? 'View only: not saved. Use Save… to keep it in the project.' : null) || entry.datasetUrl || (entry.zoomable || entry.lazy ? 'Double-click to zoom' : null) || [entry.method, entry.date, entry.campaign, entry.anomalies ? `${entry.anomalies} anomalies flagged` : ''].filter(Boolean).join(' · ') || undefined}
          className="gs-layer-row"
          onFocus={(e) => { if (e.target === e.currentTarget) onFocusRow(rowIdx); }}
          onClick={() => onFocusRow(rowIdx)}
          onDoubleClick={(e) => {
            if (e.target.closest?.('[data-no-drag]')) return;
            e.stopPropagation();
            // Lazy project datasets load (and fit the camera); everything else zooms to its extent.
            if (entry.lazy) actions.toggle(entry.key);
            else if (entry.zoomable) actions.zoomTo(entry.key);
          }}
          {...rowProps(entry.key)}
          style={{
            height: ROW_H, display: 'flex', alignItems: 'center', gap: 6, padding: showGroup ? '0 8px' : nested ? '0 8px 0 36px' : '0 8px 0 22px', cursor: 'default', userSelect: 'none',
            color: entry.visible ? 'var(--gs-text-0)' : 'var(--gs-text-3)', outline: 'none',
            background: active ? 'var(--gs-bg-3)' : undefined,
            borderLeft: `2px solid ${active ? 'var(--gs-accent)' : 'transparent'}`,
            boxShadow: dropEdge === 'above' ? 'inset 0 2px 0 var(--gs-accent)' : dropEdge === 'below' ? 'inset 0 -2px 0 var(--gs-accent)' : undefined,
            opacity: drag?.key === entry.key ? 0.5 : 1,
          }}>
          <Checkbox data-no-drag tabIndex={-1} checked={entry.visible} aria-label={`Toggle ${entry.name}`}
            onClick={(e) => e.stopPropagation()} onCheckedChange={() => actions.toggle(entry.key)}
            className="h-3.5 w-3.5 rounded-[3px]" />
          <Swatch swatch={entry.swatch} />
          {renaming ? (
            <RenameInput initial={entry.name} onDone={(v) => onRenameDone(entry.key, v)} />
          ) : (
            <span
              style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: active ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nested ? (entry.shortName || entry.name) : entry.name}
            </span>
          )}
          {entry.count != null && (
            <span data-testid="count-badge" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, lineHeight: '14px', color: 'var(--gs-text-2)', background: 'var(--gs-bg-4)', flexShrink: 0 }}>{fmtCount(entry.count)}</span>
          )}
          <span className="gs-row-hover" style={{ alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {entry.opacity != null && entry.visible && (
              <input type="range" min={10} max={100} value={Math.round(entry.opacity)} data-no-drag tabIndex={-1}
                aria-label={`Opacity of ${entry.name}`} title={`Opacity ${Math.round(entry.opacity)}%`}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => actions.setOpacity(entry.key, parseInt(e.target.value, 10))}
                style={{ width: 44, height: 3, accentColor: 'var(--gs-accent)' }} />
            )}
            {entry.zoomable && (
              <button type="button" data-no-drag tabIndex={-1} aria-label={`Zoom to ${entry.name}`} title="Zoom to layer"
                onClick={(e) => { e.stopPropagation(); actions.zoomTo(entry.key); }}
                style={{ display: 'inline-flex', padding: 1, background: 'transparent', border: 'none', color: 'var(--gs-text-2)', cursor: 'pointer' }}>
                <Crosshair size={12} />
              </button>
            )}
          </span>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56"
        onCloseAutoFocus={(e) => { if (menuCloseRef.current) { e.preventDefault(); menuCloseRef.current = false; } }}>
        <MenuItems items={items} entry={entry} onRun={run} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

/** Sub-group row: the layers derived from one dataset or model, with one checkbox for all of them. */
function SubGroupRow({ item, open, focused, onToggleOpen, onFocusRow, setRowRef, rowIdx, actions, datasetActions, menuCloseRef }) {
  const kids = item.entries;
  const on = kids.filter((e) => e.visible).length;
  const state = on === kids.length ? true : on === 0 ? false : 'indeterminate';
  const toggleAll = () => kids.forEach((e) => { if (e.visible !== (state !== true)) actions.toggle(e.key); });
  const first = kids[0];
  const dsItems = item.kind === 'dataset' && first?.datasetId ? datasetMenuItems(first) : null;
  const run = (id) => {
    if (id === 'zoom') actions.zoomToKeys?.(kids.map((e) => e.key));
    else if (id === 'remove') kids.forEach((e) => actions.remove(e.key));
    else if (id.startsWith('ds:')) { if (id === 'ds:rename') menuCloseRef.current = true; runDatasetMenuAction(id, first, datasetActions); }
  };
  const items = dsItems || [
    { id: 'zoom', label: 'Zoom to' },
    { id: 'remove', label: 'Remove all', danger: true },
  ];
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div ref={(el) => setRowRef(`s:${subCollapseKey(item.id)}`, el)} role="treeitem" aria-expanded={open} aria-level={2} aria-label={item.name}
          data-testid="layer-subgroup" data-subgroup={item.id} tabIndex={focused ? 0 : -1}
          onFocus={(e) => { if (e.target === e.currentTarget) onFocusRow(rowIdx); }}
          onClick={() => { onFocusRow(rowIdx); onToggleOpen(); }}
          style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 14px', cursor: 'pointer', userSelect: 'none', outline: 'none', color: on ? 'var(--gs-text-0)' : 'var(--gs-text-3)' }}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Checkbox data-no-drag tabIndex={-1} checked={state} aria-label={`Toggle all of ${item.name}`}
            onClick={(e) => e.stopPropagation()} onCheckedChange={toggleAll} className="h-3.5 w-3.5 rounded-[3px]" />
          <span title={item.kind === 'model' ? `Inversion model ${item.name}` : `Dataset ${item.name}`}
            style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
          <span data-testid="subgroup-count" style={{ fontSize: 9, padding: '0 5px', borderRadius: 8, lineHeight: '14px', color: 'var(--gs-text-2)', background: 'var(--gs-bg-4)', flexShrink: 0 }}>{kids.length}</span>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56"
        onCloseAutoFocus={(e) => { if (menuCloseRef.current) { e.preventDefault(); menuCloseRef.current = false; } }}>
        <MenuItems items={items} entry={first} onRun={run} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

const iconBtn = (on) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, cursor: 'pointer',
  color: on ? 'var(--gs-accent)' : 'var(--gs-text-2)', background: on ? 'var(--gs-bg-3)' : 'transparent',
  border: `1px solid ${on ? 'var(--gs-accent)' : 'var(--gs-border)'}`,
});

export default function LayerTree({
  model, campaigns = [], campaignFilter = 'all', setCampaignFilter, coordSys, annotations = [],
  activeLayerKey = null, currentProject = null,
}) {
  const { C } = useTheme();
  const { entries, order, actions, datasetActions } = model;
  const [query, setQuery] = useState('');
  const [onlyVisible, setOnlyVisible] = useState(false);
  const [flat, setFlat] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [focusIndex, setFocusIndex] = useState(0);
  const [renamingKey, setRenamingKey] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [openPos, setOpenPos] = useState(null); // Open project/dataset popover, anchored to its header button
  const openBtnRef = useRef(null);
  const closeOpen = useCallback(() => setOpenPos(null), []);
  const rowRefs = useRef(new Map());
  const kbNav = useRef(false);
  const menuCloseRef = useRef(false);

  const byKey = useMemo(() => new Map(entries.map((e) => [e.key, e])), [entries]);
  const tree = useMemo(
    () => buildTree(entries, order, { query, onlyVisible, campaign: campaignFilter, mode: flat ? 'flat' : 'grouped' }),
    [entries, order, query, onlyVisible, campaignFilter, flat],
  );
  const rows = useMemo(() => visibleRows(tree, collapsed), [tree, collapsed]);
  const rowId = (r) => (r.type === 'group' ? `g:${r.id}` : r.type === 'sub' ? `s:${r.id}` : r.key);
  const indexOf = useMemo(() => new Map(rows.map((r, i) => [rowId(r), i])), [rows]);
  const fi = Math.min(focusIndex, Math.max(rows.length - 1, 0));

  // Layers of a sub-group reorder among themselves; loose layers among the loose layers of their group.
  const subOf = useMemo(() => {
    const m = new Map();
    tree.groups.forEach((g) => g.items.forEach((it) => { if (it.type === 'sub') it.entries.forEach((e) => m.set(e.key, it.id)); }));
    return m;
  }, [tree]);
  const { drag, rowProps } = useDragReorder({
    canDrop: (from, to) => flat || (byKey.get(from)?.group === byKey.get(to)?.group && subOf.get(from) === subOf.get(to)),
    onReorder: (from, to, position) => actions.moveRelative(from, to, position),
  });

  useEffect(() => {
    if (!kbNav.current) return;
    kbNav.current = false;
    const r = rows[fi];
    if (r) rowRefs.current.get(rowId(r))?.focus();
  });

  const setRowRef = (id, el) => { if (el) rowRefs.current.set(id, el); else rowRefs.current.delete(id); };

  const onKeyDown = (e) => {
    if (e.target.tagName === 'INPUT' && e.target.type !== 'range') return;
    if (e.target.closest?.('[role="menu"]')) return;
    const intent = keyIntent(e.key, rows, fi, { collapsed, entryOf: (k) => byKey.get(k) });
    if (!intent) return;
    e.preventDefault();
    if (intent.type === 'focus') { kbNav.current = true; setFocusIndex(intent.index); }
    else if (intent.type === 'toggleGroup') setCollapsed((c) => ({ ...c, [intent.id]: intent.collapsed }));
    else if (intent.type === 'toggleVisible') actions.toggle(intent.key);
    else if (intent.type === 'remove') setConfirmKey(intent.key);
    else if (intent.type === 'rename') setRenamingKey(intent.key);
  };

  const onRenameDone = (key, value) => {
    setRenamingKey(null);
    if (value !== null && value !== byKey.get(key)?.name) actions.rename(key, value);
    const i = indexOf.get(key);
    if (i !== undefined) { kbNav.current = true; setFocusIndex(i); }
  };

  const viewing = useMemo(() => entries.filter((e) => e.group === 'viewing' && e.geojson?.features), [entries]);
  const saveDisabledWhy = !currentProject?.id ? 'Open a project to save layers' : !viewing.length ? 'Nothing to save: no layers in Viewing (not saved)' : null;
  const confirmEntry = confirmKey ? byKey.get(confirmKey) : null;
  const renderRow = (entry, showGroup, nested = false) => (
    <LayerRow key={entry.key} entry={entry} focused={rows[fi] && rowId(rows[fi]) === entry.key} active={activeLayerKey === entry.key}
      drag={drag} rowProps={rowProps} actions={actions} renaming={renamingKey === entry.key} onRenameDone={onRenameDone}
      onStartRename={setRenamingKey} onConfirmRemove={setConfirmKey} onFocusRow={setFocusIndex} setRowRef={setRowRef}
      showGroup={showGroup} rowIdx={indexOf.get(entry.key) ?? 0} menuCloseRef={menuCloseRef} datasetActions={datasetActions} nested={nested} />
  );

  return (
    <div data-testid="layer-tree" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--gs-bg-2)', color: 'var(--gs-text-0)' }}>
      <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid var(--gs-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <Search size={12} style={{ position: 'absolute', left: 7, top: 7, color: 'var(--gs-text-3)' }} />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter layers" aria-label="Filter layers"
              onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); e.stopPropagation(); }}
              style={{ width: '100%', height: 26, fontSize: 11, padding: '0 6px 0 24px', borderRadius: 6, color: 'var(--gs-text-0)', background: 'var(--gs-bg-4)', border: '1px solid var(--gs-border)', outline: 'none' }} />
          </div>
          <button type="button" style={iconBtn(onlyVisible)} aria-pressed={onlyVisible} aria-label="Show only visible layers" title="Show only visible layers" onClick={() => setOnlyVisible((v) => !v)}><Eye size={13} /></button>
          <button type="button" style={iconBtn(flat)} aria-pressed={flat} aria-label="Draw order view" title={flat ? 'Grouped view' : 'Draw order view (drag across groups)'} onClick={() => setFlat((v) => !v)}>{flat ? <List size={13} /> : <Layers size={13} />}</button>
          <button ref={openBtnRef} type="button" style={iconBtn(!!openPos)} aria-pressed={!!openPos} aria-label="Open project or dataset" title="Open a project or a dataset on the map"
            onClick={() => setOpenPos((p) => (p ? null : popoverPosBelow(openBtnRef.current)))}><FolderOpen size={13} /></button>
          <button type="button" style={iconBtn(false)} aria-label="Import file" title="Import file" onClick={() => actions.importFile()}><Plus size={13} /></button>
        </div>
        {openPos && <OpenDatasetPopover pos={openPos} wrapRef={openBtnRef} onClose={closeOpen} />}
        {campaigns.length > 1 && (
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} aria-label="Campaign filter"
            style={{ width: '100%', fontSize: 10, padding: '3px 6px', borderRadius: 6, background: 'var(--gs-bg-4)', border: '1px solid var(--gs-border)', color: 'var(--gs-text-1)', outline: 'none' }}>
            <option value="all">All Campaigns</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <div data-testid="layer-count" style={{ fontSize: 10, color: 'var(--gs-text-3)' }}>
          {tree.shown === tree.total ? `${tree.total} layer${tree.total === 1 ? '' : 's'}` : `${tree.shown} of ${tree.total} layers`}
          {flat ? ' · top of draw order first' : ''}
        </div>
      </div>

      <div role="tree" aria-label="Layers" onKeyDown={onKeyDown} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '4px 0' }}>
        {tree.shown === 0 && (
          <div style={{ padding: '12px 10px', fontSize: 11, color: 'var(--gs-text-3)' }}>{tree.total ? 'No layers match the filter.' : 'No layers yet. Use Import to add data.'}</div>
        )}
        {flat
          ? tree.flat.map((e) => renderRow(e, true))
          : tree.groups.map((g) => {
            const gi = indexOf.get(`g:${g.id}`);
            const open = !collapsed[g.id];
            return (
              <div key={g.id} role="group" aria-label={g.label}>
                <div role="treeitem" aria-expanded={open} aria-level={1} data-testid="layer-group" data-group={g.id}
                  ref={(el) => setRowRef(`g:${g.id}`, el)} tabIndex={gi !== undefined && gi === fi ? 0 : -1}
                  onFocus={(e) => { if (e.target === e.currentTarget && gi !== undefined) setFocusIndex(gi); }}
                  onClick={() => { setFocusIndex(gi ?? 0); setCollapsed((c) => ({ ...c, [g.id]: open })); }}
                  style={{ height: ROW_H - 4, display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px', cursor: 'pointer', userSelect: 'none', outline: 'none', fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--gs-text-2)' }}>
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ flex: 1 }}>{GROUP_LABEL[g.id]}</span>
                  <span style={{ fontWeight: 400 }}>{g.rows.length === g.total ? g.total : `${g.rows.length}/${g.total}`}</span>
                </div>
                {open && g.items.map((it) => {
                  if (it.type === 'layer') return renderRow(it.entry, false);
                  const ck = subCollapseKey(it.id);
                  const subOpen = !collapsed[ck];
                  const si = indexOf.get(`s:${ck}`);
                  return (
                    <div key={ck} role="group" aria-label={it.name}>
                      <SubGroupRow item={it} open={subOpen} focused={si !== undefined && si === fi} rowIdx={si ?? 0}
                        onToggleOpen={() => setCollapsed((c) => ({ ...c, [ck]: subOpen }))} onFocusRow={setFocusIndex} setRowRef={setRowRef}
                        actions={actions} datasetActions={datasetActions} menuCloseRef={menuCloseRef} />
                      {subOpen && it.entries.map((e) => renderRow(e, false, true))}
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>

      <div style={{ padding: '6px 8px', borderTop: '1px solid var(--gs-border)' }}>
        <button type="button" data-testid="layers-save" disabled={!!saveDisabledWhy} title={saveDisabledWhy || `Save ${viewing.length} viewing layer${viewing.length === 1 ? '' : 's'} to ${currentProject?.name || 'the project'}`}
          onClick={() => setSaveOpen(true)}
          style={{ width: '100%', height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: saveDisabledWhy ? 'not-allowed' : 'pointer', opacity: saveDisabledWhy ? 0.5 : 1,
            color: 'var(--gs-text-0)', background: 'var(--gs-bg-4)', border: '1px solid var(--gs-border)' }}>
          <Save size={12} /> Save…
        </button>
      </div>

      <div style={{ padding: '6px 10px', borderTop: '1px solid var(--gs-border)', fontSize: 10, color: 'var(--gs-text-3)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div data-testid="ai-banner" style={{ padding: '3px 6px', borderRadius: 6, background: C.aiBanner.bg, border: `1px solid ${C.aiBanner.border}`, color: C.aiBanner.text }}>
          AI: Suggested next line, L-09 shown on map (dashed purple)
        </div>
        <div>CRS <span style={{ color: C.link }}>{CRS_OPTIONS.find((c) => c.code === coordSys)?.label || coordSys}</span></div>
        {annotations.length > 0 && <div style={{ color: '#fbbf24' }}>{annotations.length} annotation{annotations.length !== 1 ? 's' : ''}</div>}
      </div>

      <SaveLayersDialog open={saveOpen} onClose={() => setSaveOpen(false)} layers={viewing} project={currentProject}
        onSaved={(saved) => actions.layersSaved?.(saved)} />

      <AlertDialog open={!!confirmEntry} onOpenChange={(o) => { if (!o) setConfirmKey(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove layer?</AlertDialogTitle>
            <AlertDialogDescription>{confirmEntry ? `"${confirmEntry.name}" will be removed from the map. This does not delete the source data.` : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmEntry) actions.remove(confirmEntry.key); setConfirmKey(null); }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
